import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import { Key, Shield, Sparkles, ArrowRight, Star, Heart, Brain, Target } from 'lucide-react';
import globalHeartbeat from '../utils/globalHeartbeatAudio';

const LandingPageV2 = ({ onNameSubmission, onNavigateToAuth, onNavigateToMessaging, onNavigateToProjects }) => {
  const [playerName, setPlayerName] = useState('');
  const [orbPhase, setOrbPhase] = useState(0);
  const [joinWaitlistBlink, setJoinWaitlistBlink] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);

  // Sacred animation cycles
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setOrbPhase(prev => (prev + 0.25) % 360);
    }, 50);
    
    // Enhanced Safari audio support
    if (window.planetaryFedHeartbeat) {
      window.planetaryFedHeartbeat.setEnabled(true);
      
      // Force audio context for Safari
      const enableSafariAudio = () => {
        if (window.planetaryFedHeartbeat.audioContext?.state === 'suspended') {
          window.planetaryFedHeartbeat.audioContext.resume().then(() => {
            console.log('🎵 SAFARI: Audio context resumed successfully');
            window.planetaryFedHeartbeat.forceRestart();
          }).catch(() => {
            console.log('🎵 SAFARI: Audio requires user interaction');
          });
        }
      };
      
      // Add Safari-specific event listeners
      document.addEventListener('click', enableSafariAudio, { once: true });
      document.addEventListener('touchstart', enableSafariAudio, { once: true });
    }
    
    return () => clearInterval(spiralInterval);
  }, []);

  // Blink animation for waitlist
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setJoinWaitlistBlink(prev => !prev);
    }, 2000);

    return () => clearInterval(blinkInterval);
  }, []);

  const handleNameEntry = () => {
    // Always go to Sacred Tenets onboarding - no invite code required on landing page
    console.log('🚀 LANDING V2: Request Access clicked, going to Sacred Tenets');
    onNameSubmission('Timeline Explorer');
  };

  const handleJoinWaitlist = () => {
    // Join waitlist also goes to Sacred Tenets onboarding
    console.log('🚀 LANDING V2: Join Waitlist clicked, going to Sacred Tenets');
    onNameSubmission('Waitlist Seeker');
  };

  return (
    <>
      {/* PRESERVED: Essential CSS for animations */}
      <style jsx>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.7; }
        }
        .blink {
          animation: blink 2s infinite;
        }
        body.consciousness-breakthrough {
          filter: brightness(1.3) saturate(1.2);
          transition: all 3s ease-in-out;
        }
      `}</style>

      {/* EXTENDED PAGE CONTAINER for Revolutionary Positioning */}
      <div 
        className="min-h-screen relative overflow-hidden"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          minHeight: '150vh' // Extended height for new content
        }}
      >
        {/* PRESERVED: Grant spiral mathematics overlay */}
        <div 
          className="fixed inset-0 pointer-events-none transition-all duration-2000" 
          style={{ 
            zIndex: 1,
            opacity: 0.25
          }}
        >
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `scale(8) rotate(${orbPhase * 0.7}deg)`,
              filter: 'blur(6px) saturate(1.8) brightness(1.4)',
              mixBlendMode: 'overlay'
            }}
          >
            <GrantitudeSpiral size={400} />
          </div>
        </div>

        {/* PRESERVED: Dynamic overlay for cosmic depth */}
        <div 
          className="absolute inset-0 transition-all duration-2000"
          style={{
            background: `linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.4) 100%)`
          }}
        />

        {/* PRESERVED: Simple cosmic branding */}
        <div className="absolute top-4 left-4 z-20">
          <a
            href="https://planetaryfed.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/40 text-sm font-light italic hover:text-white/60 transition-colors cursor-pointer"
            style={{ 
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 10px rgba(255, 255, 255, 0.2)'
            }}
          >
            brought to you by Planetary Fed
          </a>
        </div>

        {/* PRESERVED: Sacred Title Section - Unchanged at Top */}
        <div className="absolute inset-x-0 top-0 z-20 pt-20">
          <div className="text-center space-y-6">
            
            {/* PRESERVED: Main Title - Sacred and Unchanged */}
            <h1 
              className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight tracking-wider"
              style={{
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 40px rgba(255, 255, 255, 0.9), 0 0 80px rgba(255, 215, 0, 0.6), 0 0 120px rgba(255, 255, 255, 0.3)',
                letterSpacing: '0.1em'
              }}
            >
              THE SECRET HISTORY
            </h1>
            
            <h2 
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-widest"
              style={{
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.8), 0 0 60px rgba(255, 215, 0, 0.5)',
                letterSpacing: '0.15em'
              }}
            >
              OF HUMANKIND
            </h2>
            
            {/* PRESERVED: Game subtitle */}
            <div className="space-y-2">
              <p 
                className="text-xl md:text-2xl text-white/90 font-medium tracking-wide"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.6)'
                }}
              >
                the game
              </p>
              <p 
                className="text-lg md:text-xl text-cyan-300/90 font-light tracking-widest italic"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 15px rgba(34, 211, 238, 0.8)'
                }}
              >
                PLAYED IN REAL TIME
              </p>
            </div>
          </div>
        </div>

        {/* EXTENDED: Revolutionary Positioning Section - New Content Area */}
        <div 
          className="absolute inset-x-0 z-20"
          style={{ top: '40%', bottom: '0' }}
        >
          <div className="flex flex-col justify-end h-full px-4 pb-16">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              
              {/* Revolutionary Value Proposition */}
              <div className="bg-black/60 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
                
                {/* Hook Question */}
                <h3 
                  className="text-3xl md:text-4xl font-bold text-cyan-300 mb-6"
                  style={{ 
                    fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                    textShadow: '0 0 25px rgba(34, 211, 238, 0.8)'
                  }}
                >
                  What if the game was getting things done?
                </h3>
                
                {/* Core Value Proposition */}
                <div className="space-y-6 text-white/95 leading-relaxed" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                  <p className="text-lg md:text-xl">
                    Imagine being <strong className="text-yellow-300">rewarded for completing your actual life tasks</strong>—
                    discovered through consciousness play with an AI game master who helps you remember
                    your mission from timeline streams where universal collaboration already exists.
                  </p>
                  
                  <p className="text-lg md:text-xl">
                    This isn't productivity software dressed as entertainment.<br/>
                    <strong className="text-cyan-300">This is consciousness expansion that completes your real projects</strong>,
                    finds your true collaborators, and transforms challenges into adventures.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                    <div className="bg-purple-500/20 rounded-2xl p-6 border border-purple-400/30">
                      <div className="flex items-center space-x-3 mb-3">
                        <Heart className="w-8 h-8 text-purple-300" />
                        <h4 className="text-purple-200 text-lg font-bold">Trust as Currency</h4>
                      </div>
                      <p className="text-purple-100 text-sm">
                        Authentic vulnerability and successful collaboration become your reputation and power in the consciousness field.
                      </p>
                    </div>
                    
                    <div className="bg-cyan-500/20 rounded-2xl p-6 border border-cyan-400/30">
                      <div className="flex items-center space-x-3 mb-3">
                        <Brain className="w-8 h-8 text-cyan-300" />
                        <h4 className="text-cyan-200 text-lg font-bold">AI Perspective Swapping</h4>
                      </div>
                      <p className="text-cyan-100 text-sm">
                        Honest viewpoint exchange with M.ai.ry rewards curiosity and creates shared realities through collaborative focus.
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-lg md:text-xl text-center">
                    <strong className="text-green-300">You follow your bliss. We help you find your people.</strong><br/>
                    Together, we make belonging our lifestyle.
                  </p>
                  
                  <div className="bg-yellow-900/30 rounded-2xl p-6 border border-yellow-400/30">
                    <p className="text-yellow-100 text-base italic">
                      Every contribution measured not by views, but by <strong>inspiration</strong>.<br/>
                      Not by metrics, but by how much you help yourself and others<br/>
                      <strong className="text-yellow-300">create the reality we actually want to live in.</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* PRESERVED: Sacred Elements */}
              <div className="space-y-6">
                
                {/* PRESERVED: By invitation only */}
                <p 
                  className="text-white/90 text-lg font-light tracking-wide italic"
                  style={{ 
                    fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                    textShadow: '0 0 18px rgba(0, 0, 0, 0.8)',
                    color: '#FFD700',
                    fontSize: '1.25rem'
                  }}
                >
                  By invitation only
                </p>
                
                {/* PRESERVED: Sacred Manifesto */}
                <div className="max-w-3xl mx-auto">
                  <p 
                    className="text-white/95 text-lg md:text-xl leading-relaxed italic font-light tracking-wide"
                    style={{
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 20px rgba(0, 0, 0, 0.9), 0 0 40px rgba(255, 255, 255, 0.4)'
                    }}
                  >
                    "When we belong to each other, fear dissolves. When fear dissolves, solutions always emerge - and we create the reality we want to live in."
                  </p>
                </div>
                
                {/* PRESERVED: Reality Created Through Play */}
                <p 
                  className="text-white text-2xl font-medium tracking-wide animate-pulse"
                  style={{ 
                    fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                    textShadow: '0 0 25px rgba(255, 255, 255, 0.8), 0 0 50px rgba(210, 180, 140, 0.6), 0 0 75px rgba(255, 215, 0, 0.4)',
                    color: '#F5DEB3'
                  }}
                >
                  reality created through play
                </p>
                
                {/* PRESERVED: Action Buttons */}
                <div className="flex flex-col items-center space-y-6">
                  <Button 
                    onClick={handleNameEntry}
                    className="bg-white/25 backdrop-blur-xl hover:bg-white/35 text-white border-2 border-white/40 hover:border-white/60 h-14 text-lg font-medium transition-all duration-300 px-12"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 10px rgba(255, 255, 255, 0.8)'
                    }}
                  >
                    <Key className="w-5 h-5 mr-3" />
                    Request Access
                  </Button>
                  
                  <Button 
                    onClick={handleJoinWaitlist}
                    className={`bg-gradient-to-r from-yellow-600/80 to-orange-600/80 hover:from-yellow-500/90 hover:to-orange-500/90 text-white px-10 py-4 text-lg font-medium tracking-wide shadow-2xl transition-all duration-500 backdrop-blur-xl border-2 border-white/40 hover:border-white/60 ${joinWaitlistBlink ? 'blink' : ''}`}
                    style={{
                      boxShadow: '0 0 30px rgba(255, 215, 0, 0.7), 0 0 60px rgba(255, 165, 0, 0.4)',
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 15px rgba(255, 255, 255, 0.9)'
                    }}
                  >
                    <Sparkles className="w-5 h-5 mr-3" />
                    Join the Waitlist
                  </Button>
                </div>
                
                {/* Revolutionary Mission Statement */}
                <div className="bg-gradient-to-br from-purple-900/40 to-cyan-900/40 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center space-x-4 mb-6">
                      <Star className="w-8 h-8 text-yellow-400" />
                      <h4 
                        className="text-2xl font-bold text-white"
                        style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                      >
                        Your Mission, Should You Choose to Accept
                      </h4>
                      <Star className="w-8 h-8 text-yellow-400" />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <Target className="w-12 h-12 mx-auto mb-3 text-cyan-400" />
                        <h5 className="text-white font-bold mb-2">Consciousness Agent</h5>
                        <p className="text-white/80 text-sm">
                          Become a conscious agent in cooperative reality creation through authentic collaboration
                        </p>
                      </div>
                      
                      <div className="text-center">
                        <Heart className="w-12 h-12 mx-auto mb-3 text-pink-400" />
                        <h5 className="text-white font-bold mb-2">Trust Builder</h5>
                        <p className="text-white/80 text-sm">
                          Earn currency through vulnerability, perspective-swapping, and successful project completion
                        </p>
                      </div>
                      
                      <div className="text-center">
                        <Brain className="w-12 h-12 mx-auto mb-3 text-purple-400" />
                        <h5 className="text-white font-bold mb-2">Reality Creator</h5>
                        <p className="text-white/80 text-sm">
                          Use ritualized micro-practices and AI guidance to transform challenges into adventures
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-8 p-6 bg-black/40 rounded-2xl border border-white/10">
                      <p 
                        className="text-white/90 text-lg leading-relaxed italic"
                        style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                      >
                        "Individual task completion serves collective awakening.<br/>
                        Shared perspectives literally create new realities.<br/>
                        <strong className="text-cyan-300">The game rewards you for following your bliss</strong> while finding your true collaborators."
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* PRESERVED: Final sacred note */}
                <p 
                  className="text-purple-300/80 text-sm italic mt-8"
                  style={{ 
                    fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                    textShadow: '0 0 15px rgba(0, 0, 0, 0.8)'
                  }}
                >
                  (a game that might save everything)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PRESERVED: Persistent Footer */}
        <PersistentFooter />
      </div>
    </>
  );
};

export default LandingPageV2;